import { getUserDetails } from "../../../app/api/getUserDetails";
import { postRetrieveBookingSearch } from "../../../app/api/postRetrieveBooking";
import { updateCustomerById } from "../../../app/api/updateCustomerById";
import { BaseAction } from "../../../core/actions/BaseAction";
import { getTranslatedMessage } from "../../../Lib/Utils/i18n";
import { isActionRunSuccessfully } from "../../../Lib/Utils/validationUtil";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IApiResponse, IBookingSearchResult, IBookingsSearchResponse, IRetrieveBookingSearchRequest, IUserResponse } from "../../../Orchestrator/Types/Index";
import { IDefaultActionResult } from "../../../Orchestrator/Types/Interface/ActionParams/DefaultActionResult";
import { IRetrieveBookingAPICallActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/RetrieveBookingParams";
import { showNotification } from "../../../Services/notificationService";
import { getBookingIsPastOrUpcoming } from "../Booking/GetBookingIsPastOrUpcoming";
import { bookingsPastPivotDubai } from "../../../Lib/Utils/bookingAppearancePeriod";
import { upcomingPastBookingTabAction } from "../Booking/UpcomingPastBookingTabAction";
import { getBookingDetailOfRetrieveBookingAction } from "./GetBookingDetailOfRetrieveBookingAction";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

class RetrieveBookingAPICallAction extends BaseAction<IDefaultActionResult, IRetrieveBookingAPICallActionParams> {
    private getCurrentActiveTab(): "1" | "2" {
        const activeTab = getRecoilStateValue<string>("activeBookingTabAtom", "1");
        return activeTab === "2" ? "2" : "1";
    }

    private isMixedBooking(result: IBookingSearchResult): boolean {
        const validTripRefs = (result.bookedOfferSummaries || [])
            .filter(summary => !(summary.bookingPartSummaries || []).some(part => part.status === "EXCHANGED" || part.status === "EXCHANGE_ONGOING"))
            .map(summary => summary.tripRef);

        const validTrips = (result.tripSummaries || []).filter(trip => validTripRefs.includes(trip.id));
        if (!validTrips.length) return false;

        // The appearance pivot, not the raw clock. This decides WHICH TAB the
        // retrieved booking is searched into, so it has to call "past" exactly
        // when the two search ranges do. On the real clock, a round trip whose
        // outbound left 20 minutes ago reads as mixed, routes to Past, and is
        // then excluded by the Past range (which now ends one period back) —
        // the booking would come back in neither tab. Warm by here: the caller
        // awaits getBookingIsPastOrUpcoming just above.
        const now = bookingsPastPivotDubai();
        const hasPastTrip = validTrips.some(trip => dayjs(trip.startTime).tz("Asia/Dubai").isBefore(now));
        const hasUpcomingTrip = validTrips.some(trip => !dayjs(trip.startTime).tz("Asia/Dubai").isBefore(now));

        return hasPastTrip && hasUpcomingTrip;
    }

    private resolveActiveTab(isPastBooking: boolean, result: IBookingSearchResult): "1" | "2" {
        if (this.isMixedBooking(result)) {
            return this.getCurrentActiveTab();
        }
        return isPastBooking ? "2" : "1";
    }

    async recursiveForAutoScroll(rbBookingId: string, isPastBooking: boolean) {
        const interval = 2_000;  // 2 000 ms  = 2 s
        const maxTime = 10_000; // 10 000 ms = 10 s
        const startedAt = performance.now();
        return new Promise((resolve, reject) => {
            (function probe() {
                const whichTabIs = isPastBooking ? "rc-tabs-0-panel-2" : "rc-tabs-0-panel-1"
                const parent = document.getElementById(whichTabIs)
                if (parent) {
                    const node = parent.querySelectorAll(`[id*="${CSS.escape(rbBookingId)}"]`);
                    if (node.length > 0) {
                        node[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
                        resolve(node);
                        return;
                    }
                    if (performance.now() - startedAt >= maxTime) {
                        reject();
                        return;
                    }

                    setTimeout(probe, interval);
                }
            })();
        });
    }
    override async run(): Promise<IDefaultActionResult> {
        let actionResult: IDefaultActionResult = {
            data: [],
            message: "",
            status: "fail"
        }
        let rbBookingId = ""
        let isPastBooking = false

        try {
            const { bookingReference, lastName, isAnonymous = false, bookingIds = [] } = this.params!
            if (bookingReference && lastName) {
                const searchRequest: IRetrieveBookingSearchRequest = {
                    reference: bookingReference || "",
                    purchaserPassengerName: lastName
                };

                const response: any = await postRetrieveBookingSearch(searchRequest);
                if (response.status === 'ok' && response.answer) {
                    const bookingResults: IBookingsSearchResponse = response.answer;
                    actionResult.data = bookingResults
                    // The search endpoint matches on passenger name and can return
                    // several bookings that share the surname while ignoring the
                    // reference. Pick the one whose code actually matches the entered
                    // reference instead of blindly taking the first result — otherwise
                    // we operate on (and dedupe against) the wrong booking.
                    const normalizedReference = (bookingReference || "").trim().toUpperCase()
                    const matchedResult: IBookingSearchResult | undefined = bookingResults.bookingSearchResults
                        .find((b) => (b.code || "").trim().toUpperCase() === normalizedReference)
                    actionResult.status = matchedResult ? "success" : "fail"
                    if (actionResult.status === "success" && matchedResult) {
                        const result: IBookingSearchResult = matchedResult
                        rbBookingId = result.id
                        getBookingIsPastOrUpcoming.setParams(result)
                        await getBookingIsPastOrUpcoming.execute()
                        if (isActionRunSuccessfully(getBookingIsPastOrUpcoming)) {
                            const result = getBookingIsPastOrUpcoming.result!
                            isPastBooking = result?.data || false
                            // isPastBooking = false
                        }
                        const activeTab = this.resolveActiveTab(isPastBooking, result);
                        if (!isAnonymous) {
                            //Set Retrieve booking ID to the current user account
                            const response: IApiResponse<IUserResponse> = await getUserDetails();
                            if (response.status === "ok" && response.answer) {
                                let userData: IUserResponse = response.answer;

                                const bookingId: string = result.id
                                let retrieveBookingIsAlreadyLink = (userData?.bookings || []).includes(bookingId)
                                if (!retrieveBookingIsAlreadyLink) {
                                    retrieveBookingIsAlreadyLink = bookingIds.includes(bookingId)
                                }
                                if (!retrieveBookingIsAlreadyLink) {
                                    userData = {
                                        ...userData,
                                        bookings: [...(userData?.bookings || []), bookingId]
                                    }

                                    const response = await updateCustomerById(userData.id.toString(), {
                                        bookings: [...(userData?.bookings || [])]
                                    });
                                    if (response.status === "ok" && response.answer) {
                                        actionResult.status = "success"
                                        upcomingPastBookingTabAction.setParams({
                                            activeTab,
                                            isAnonymous: false,
                                            retrieveBookingsIds: [...(userData?.bookings || [])]
                                        })
                                        await upcomingPastBookingTabAction.execute();
                                    } else {
                                        actionResult.status = "fail"
                                        actionResult.message = "User not modified"
                                    }
                                } else {
                                    actionResult.data = {
                                        ...actionResult.data || {},
                                        alreadyExists: true
                                    }
                                    showNotification("info", getTranslatedMessage("bookingAlreadyInAccount"), "")
                                    upcomingPastBookingTabAction.setParams({
                                        activeTab,
                                        isAnonymous: false,
                                        retrieveBookingsIds: [...(userData?.bookings || [])]
                                    })
                                    await upcomingPastBookingTabAction.execute();
                                }
                                let updatedUserData: IUserResponse = {
                                    ...userData,
                                    firstName: userData.firstName === "-" ? "" : userData.firstName,
                                    lastName: userData.lastName === "-" ? "" : userData.lastName,
                                };
                                updateRecoilState<IUserResponse>("userDataAtom", updatedUserData);
                                updateRecoilState("showRetrieveBookingAtom", false)
                            } else {
                                actionResult.status = "fail"
                                actionResult.message = "User not found"
                            }
                        } else {
                            /**
                             * Else part handle gust user RB caching data.
                            */
                            if (isPastBooking) {
                                actionResult.status = "fail";
                                actionResult.data.isPastBooking = true
                            } else {
                                const cacheData = {
                                    bookingId: result.id,
                                    bookingReference,
                                    lastName,
                                    isPastBooking,
                                    activeTab
                                }
                                try {
                                    let alreadyCashedData: any = localStorage.getItem("retrieveBookingCacheData")
                                    if (alreadyCashedData) {
                                        let needToCallAPI = true
                                        alreadyCashedData = JSON.parse(alreadyCashedData)
                                        if (alreadyCashedData && alreadyCashedData?.retrieveBooking) {
                                            const alreadyRetrieve = alreadyCashedData.retrieveBooking.bookingId === result.id
                                            const retrieveBookingCacheData: any = {
                                                ...alreadyCashedData
                                            }
                                            if (!alreadyRetrieve) {
                                                retrieveBookingCacheData.retrieveBooking = cacheData
                                                needToCallAPI = true
                                            }
                                            if (!(alreadyCashedData?.reference || []).includes(cacheData.bookingReference)) {
                                                retrieveBookingCacheData.reference = [
                                                    ...(alreadyCashedData?.reference || []),
                                                    cacheData.bookingReference
                                                ]
                                            }
                                            if (!(alreadyCashedData?.lastName || []).includes(cacheData.lastName)) {
                                                retrieveBookingCacheData.lastName = [
                                                    ...alreadyCashedData?.lastName || [],
                                                    cacheData.lastName
                                                ]
                                            }
                                            localStorage.setItem("retrieveBookingCacheData", JSON.stringify(retrieveBookingCacheData));
                                            if (alreadyRetrieve) {
                                                actionResult.data = {
                                                    ...actionResult.data || {},
                                                    alreadyExists: true
                                                }
                                                showNotification("info", getTranslatedMessage("bookingAlreadyInAccount"), "")
                                            }
                                            if (needToCallAPI)
                                                await getBookingDetailOfRetrieveBookingAction.execute()
                                        }
                                    } else {
                                        localStorage.setItem("retrieveBookingCacheData", JSON.stringify({
                                            retrieveBooking: cacheData,
                                            reference: [cacheData.bookingReference],
                                            lastName: [cacheData.lastName]
                                        }))
                                        await getBookingDetailOfRetrieveBookingAction.execute()
                                    }
                                } catch (error) {
                                }
                            }
                        }
                    }
                } else {
                    console.error("No results found or response is not ok:", response);
                }
            } else {
                actionResult.message = "Reference or last name must be required"
            }
        } catch (error) {
            actionResult.message = "Something went wrong"
        } finally {
            if (actionResult.status === "success" && rbBookingId != "") {
                this.recursiveForAutoScroll(rbBookingId, isPastBooking)
            }
            return actionResult
        }
    }
}

export const retrieveBookingAPICallAction = new RetrieveBookingAPICallAction()